import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import {
  RichTextEditor,
  TOOLBAR_BUTTONS,
  Toolbar,
  BoldPlugin,
  ItalicPlugin,
  LinkPlugin,
  SubScriptPlugin,
  SuperScriptPlugin
} from "widgets/richtext";

import { Base } from "../base";
import { TextArea as TextAreaReadonly } from "../../renderers/textarea";

import styles from "./textarea.css";

const TEXTAREA_TOOLBAR_BUTTONS = [
  TOOLBAR_BUTTONS.BOLD,
  TOOLBAR_BUTTONS.ITALIC,
  TOOLBAR_BUTTONS.SUBSCRIPT,
  TOOLBAR_BUTTONS.SUPERSCRIPT,
  TOOLBAR_BUTTONS.LINK,
  TOOLBAR_BUTTONS.ERASE
];

export function TextArea(props) {
  const { column, value, pos } = props;
  const baseRef = useRef(null);
  const editorRef = useRef(null);
  const [readOnly, setReadOnly] = useState(true);
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(
    function onChangeValue() {
      setCurrentValue(value || "");
    },
    [value]
  );

  useEffect(
    function onPositionChange() {
      setReadOnly(true);
    },
    [pos]
  );

  const handleBlurEvent = (e) => {
    if (editorRef.current) {
      baseRef.current.dataChanged(editorRef.current.getContents());
      baseRef.current.dataCompleted();
    }
  };

  const handleChangeEvent = (e) => {
    if (editorRef.current) {
      baseRef.current.dataChanged(editorRef.current.getContents());
    }
  };

  return (
    <Base
      {...props}
      ref={baseRef}
      onEditing={(value) => {
        setReadOnly(false);
        if (value) {
          setCurrentValue(value);
        }
        setTimeout(() => {
          if (value) {
            baseRef.current.dataChanged(value);
          }
          editorRef.current?.getInstance()?.focus();
        });
      }}
      type="textarea"
      doubleClickToEdit={false}
    >
      {(readOnly || column.readOnly) && (
        <TextAreaReadonly
          {...props}
          style={{
            lineHeight: 1.67,
            padding: "0.8rem",
            width: `${column.width}px`,
            wordBreak: "break-word"
          }}
          onDoubleClick={() => {
            !column.readOnly && baseRef.current.startEditing();
          }}
        />
      )}
      {!readOnly && !column.readOnly && (
        <React.Fragment>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                baseRef.current.dataCompleted();
                e.preventDefault();
                if (e.shiftKey) {
                  baseRef.current.movePrevious();
                } else {
                  baseRef.current.moveNext();
                }
              }
              e.stopPropagation();
            }}
          >
            <RichTextEditor
              ref={editorRef}
              markdown={true}
              value={currentValue}
              className={`${styles.content} base`}
              focus={true}
              onBlur={handleBlurEvent}
              onChange={handleChangeEvent}
              inlineToolbar={false}
              allowSlashCommand={false}
              plugins={[
                BoldPlugin,
                ItalicPlugin,
                LinkPlugin,
                SubScriptPlugin,
                SuperScriptPlugin
              ]}
              placeholder={""}
            />
            <Toolbar
              toolbarButtons={TEXTAREA_TOOLBAR_BUTTONS}
              toolbarRef={editorRef}
              inline={true}
              className={styles.toolbar}
            />
          </div>
        </React.Fragment>
      )}
    </Base>
  );
}

TextArea.propTypes = {
  value: PropTypes.any,
  column: PropTypes.any,
  pos: PropTypes.any
};
